import { useState } from "react";
import { Sparkles, Loader2, X } from "lucide-react";
import { Button } from "../ui/button";
import { createCheckout, clearEntitlement } from "../../lib/pro";
import { toast } from "sonner";

/**
 * Inline banner that appears at the top of /report when the visitor's
 * previous Pro Pass has expired. Surfaces:
 *   1. WHEN their pass ended (so they recognise it as theirs)
 *   2. A direct "Renew for $29" button that kicks off Stripe checkout
 *   3. A dismiss control (sets a session flag so it doesn't nag on every
 *      navigation within the same tab).
 *
 * We deliberately do NOT block the page — the user can still read their
 * report; only the gated Pro sections (AI memo rewrites, fresh PDF) stay
 * locked behind the renew CTA shown elsewhere.
 */
export default function ExpiredPassBanner({ entitlement }) {
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem("cfg_renew_dismissed") === "1"
  );

  if (!entitlement || dismissed) return null;

  const expiredOn = entitlement.expires_at
    ? new Date(entitlement.expires_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : null;

  async function handleRenew() {
    setBusy(true);
    try {
      const session = await createCheckout({ packageId: "pro_30d" });
      if (session?.url) {
        window.location.href = session.url;
      } else {
        toast.error("Couldn't start checkout. Please try again.");
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
      setBusy(false);
    }
  }

  function handleDismiss() {
    sessionStorage.setItem("cfg_renew_dismissed", "1");
    setDismissed(true);
  }

  function handleStartFresh() {
    clearEntitlement();
    setDismissed(true);
    toast.message("Cleared. You're browsing as a new visitor now.");
  }

  return (
    <div
      className="no-print mt-6 rounded-xl border border-[#9c6b1d]/30 bg-gradient-to-br from-[#FDF8F1] to-[#F7F5F0] p-5 sm:p-6 relative"
      data-testid="expired-pass-banner"
    >
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-[#5C665D] hover:text-[#1F382E] transition-colors"
        data-testid="expired-pass-dismiss"
      >
        <X size={16} strokeWidth={1.5} />
      </button>

      <div className="flex items-start gap-3 sm:gap-4">
        <div className="hidden sm:flex shrink-0 w-10 h-10 rounded-full bg-[#9c6b1d]/10 items-center justify-center">
          <Sparkles size={18} strokeWidth={1.5} className="text-[#9c6b1d]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#9c6b1d] font-medium">
            Your Pro Pass ended
          </p>
          <p className="font-serif-display text-xl sm:text-2xl text-[#1F382E] mt-1 tracking-tight">
            {expiredOn
              ? `Your 30-day Pro Pass ended ${expiredOn}.`
              : "Your 30-day Pro Pass has ended."}
          </p>
          <p className="text-sm text-[#5C665D] mt-1.5 leading-relaxed">
            Renew for $29 to unlock the AI memo, tone rewrites, and PDF exports for another 30 days.
            Your saved reports and trends stay where they are.
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              onClick={handleRenew}
              disabled={busy}
              className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md h-10 px-5 transition-all duration-300"
              data-testid="expired-pass-renew-btn"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Starting checkout…
                </>
              ) : (
                "Renew for $29"
              )}
            </Button>
            <button
              onClick={handleStartFresh}
              className="text-[12px] text-[#5C665D] hover:text-[#1F382E] underline underline-offset-2 transition-colors"
              data-testid="expired-pass-start-fresh"
            >
              No thanks, browse as a new visitor
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
